import { HTMLAttributes, ReactNode, useState } from 'react';

import { ArrowLeftOutlined, CloseOutlined, LockOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import { Link } from 'react-router-dom';

import { ROLES } from '../../../../../helpers/utils/auth';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import styles from '../styles/AdMenu.module.scss';
import { TabType } from '../types';

type Item = {
  key: string;
  label: ReactNode;
  url?: string;
  withoutClose?: boolean;
  onClick?: () => void;
  style?: HTMLAttributes<HTMLDivElement>['style'];
  permission?: string[];
};

export type DynamicMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  activeTab: TabType;
  items?: Array<Item>;
  collapsed: boolean;
  nextLevel?: Omit<
    DynamicMenuProps,
    | 'onClose'
    | 'activeMainTab'
    | 'activeTab'
    | 'isOpen'
    | 'collapsed'
    | 'setActiveTab'
  >;
  level?: number;
};

const MenuItem: React.FC<{
  item: Item;
  activeTab: TabType;
  onClose: () => void;
}> = ({ item, activeTab, onClose }) => {
  const role = useTypedSelector((state) => state.currentUser.role);
  const havenPermisson = !item.permission || role === ROLES.super
    ? true
    : item.permission.includes(role);
  const [ open, setOpen ] = useState(false);

  const className = `${styles['menu-item']} ${
    activeTab === item.key ? styles['active'] : ''
  } ${havenPermisson ? '' : styles['menu-item-disabled']}`;

  const handleClick = () => {
    item.onClick?.();
    !item.withoutClose && onClose();
  };

  if (item.onClick) {
    return (
      <div className={className} onClick={handleClick} style={item.style}>
        {item.label}
      </div>
    );
  }

  return havenPermisson ? (
    <Link to={item.url || '#'} className={className} onClick={handleClick}>
      {item.label}
    </Link>
  ) : (
    <>
      <div
        className={className}
        style={item.style}
        onClick={() => setOpen(true)}
      >
        {item.label} <LockOutlined />
      </div>
      <Modal
        open={open}
        onOk={() => setOpen(false)}
        okText='Ок'
        cancelText
        centered
		closable
		maskClosable
		destroyOnClose
		closeIcon={<CloseOutlined onClick={() => setOpen(false)}/>}
      >
        К выбранному разделу у Вас доступ закрыт.
        <br />
        Обратитесь к руководству, чтобы Вам расширили права.
      </Modal>
    </>
  );
};

const DynamicMenu: React.FC<DynamicMenuProps> = ({
  isOpen,
  onClose,
  activeTab,
  items,
  collapsed,
  nextLevel,
  level = 1,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className={`${styles['side-menu']} ${
        level === 1 ? styles['side-menu-lvl-1'] : ''
      } ${collapsed ? styles['side-menu-collapsed'] : ''}`}
    >
      <div className={styles['side-menu-content']}>
        {level === 1 && (
          <Button
            className={styles['closed-button']}
            icon={<ArrowLeftOutlined />}
            onClick={onClose}
          />
        )}
        {items && (
          <div className={styles['menu-section']}>
            {items.map((item) => (
              <MenuItem
                key={item.key}
                item={item}
                activeTab={activeTab}
                onClose={onClose}
              />
            ))}
          </div>
        )}
      </div>
      {nextLevel && (
        <DynamicMenu
          {...nextLevel}
          level={level + 1}
          isOpen={isOpen}
          onClose={onClose}
          activeTab={activeTab}
          collapsed={collapsed}
        />
      )}
      <div className={styles['side-menu-overlay']} onClick={onClose} />
    </div>
  );
};

export default DynamicMenu;
